import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { Search, ShoppingBag, Heart, User as UserIcon, Menu, X, LogOut, Shield, ShieldCheck, Bell, Award, Copy, ChevronDown, Sparkles, Home, Package, MapPin, Palette, Layers, ChevronLeft, ChevronRight, Phone, Mail, Trophy, Tag, Star, Zap, Gift, Crown, Shirt, Flame, Rocket, Truck, Mars, Venus, ScanFace } from "lucide-react";
import { HEADER } from "@/constants/testIds";
import Logo from "@/components/Logo";
import api from "@/lib/api";
import { toast } from "sonner";
import { useSiteSettings } from "@/components/SiteSettings";
import { useTaxonomy } from "@/context/TaxonomyContext";
import { useGender } from "@/context/GenderContext";
import { MEGA_MENU } from "@/constants/taxonomy";
import { useHeaderNav } from "@/hooks/useHeaderNav";

// Map of lucide icon names → React components. Whitelist mirrors
// SUPPORTED_ICONS in /app/backend/routes_header_nav.py so admin selections
// always resolve. Unknown names render no icon (graceful fallback).
const ICON_BY_NAME = {
  ShoppingBag, Palette, Heart, ShieldCheck, Sparkles, Trophy,
  Tag, Star, Zap, Gift, Award, Crown, Shirt, Layers, Flame, Rocket,
  Package, Truck,
};

// Editorial style → Tailwind class bundle. Keep classes inline so the admin
// only picks the intent ("highlight" / "primary" / "default") and we own the
// final visual treatment in one place.
function navClassFor(style, isMobile = false) {
  if (isMobile) return ""; // mobile renderer uses its own component
  if (style === "highlight") return "text-[#FF5722] font-bold uppercase tracking-wider text-xs hover:opacity-80 transition-colors inline-flex items-center gap-1";
  if (style === "primary")   return "text-[#FF5722] font-semibold hover:opacity-80 transition-opacity inline-flex items-center gap-1";
  return "text-zinc-700 hover:text-[#FF5722] transition-colors inline-flex items-center gap-1";
}

// Default fallback used when the API hasn't loaded yet OR returns empty —
// keeps the old hardcoded experience as a safe baseline.
const FALLBACK_NAV = [
  { id: "shop",        label: "Shop",         href: "/shop",              icon: null,          style: "default"   },
  { id: "customize",   label: "Customize",    href: "/custom-shop",       icon: "Palette",     style: "default"   },
  { id: "couple",      label: "Couple",       href: "/couple-wear",       icon: "Heart",       style: "default"   },
  { id: "heavy-duty",  label: "Heavy Duty",   href: "/heavy-duty",        icon: "ShieldCheck", style: "highlight" },
  { id: "sell",        label: "Sell Designs", href: "/sell-your-designs", icon: "Sparkles",    style: "primary"   },
  { id: "leaderboard", label: "Leaderboard",  href: "/leaderboard",       icon: "Trophy",      style: "default"   },
];

export default function Header() {
  const { user, logout } = useAuth();
  const { cart, wishlist } = useCart();
  const settings = useSiteSettings();
  const taxonomy = useTaxonomy();
  // Mega-menu strip respects the same `is_visible` toggle that admins flip in
  // /admin/menu-settings. Items missing the field (legacy data) default to
  // visible so we never accidentally blank the whole strip after a schema
  // migration. The fallback constant has no is_visible flags so the OR makes
  // it pass through unfiltered.
  const menuItems = (taxonomy?.menu_items && taxonomy.menu_items.length)
    ? taxonomy.menu_items.filter((m) => m && m.is_visible !== false)
    : MEGA_MENU;
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  // Header nav comes from the shared `useHeaderNav` hook so the Footer can
  // mirror visibility toggles without re-fetching or drifting out of sync.
  // Falls back to FALLBACK_NAV until the API resolves (first paint never empty).
  const { items: navFromApi } = useHeaderNav();
  const navItems = (navFromApi && navFromApi.length > 0) ? navFromApi : FALLBACK_NAV;
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState({ items: [], unread: 0 });
  const [megaOpenIdx, setMegaOpenIdx] = useState(null);
  // Shared MEN | WOMEN | COUPLE pill toggle — drives the home hero campaign
  // strip filtering. Sits in the mega-menu strip (left side) so it's globally
  // accessible and persists as the customer browses.
  const { gender, setGender } = useGender();
  // Tracks whether the mega-menu strip has hidden items off the left/right
  // edge — drives the gradient fade overlays + chevron scroll buttons so
  // users notice they can scroll horizontally to see more categories.
  const megaScrollRef = useRef(null);
  const [megaEdges, setMegaEdges] = useState({ left: false, right: false });

  useEffect(() => {
    const el = megaScrollRef.current;
    if (!el) return;
    const update = () => {
      const left = el.scrollLeft > 1;
      const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
      setMegaEdges((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
    };
    update();
    // Re-measure once after layout settles (smooth-scroll animations end ~300ms)
    const settleTimer = setTimeout(update, 350);
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    // ResizeObserver catches strip width changes from taxonomy async loads
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    ro?.observe(el);
    return () => {
      clearTimeout(settleTimer);
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      ro?.disconnect();
    };
  }, [menuItems]);

  const scrollMega = (dir) => {
    const el = megaScrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(200, el.clientWidth * 0.6), behavior: "smooth" });
  };
  const [mobileMegaIdx, setMobileMegaIdx] = useState(null);
  const searchInputRef = useRef(null);
  const notifRef = useRef(null);

  // Initial load when user changes
  useEffect(() => {
    if (!user) {
      setNotifications({ items: [], unread: 0 });
      return;
    }
    api.get("/me/notifications")
      .then((r) => setNotifications(r.data || { items: [], unread: 0 }))
      .catch(() => {});
  }, [user]);

  // Background polling (every 60s) while logged in
  useEffect(() => {
    if (!user) return undefined;
    const t = setInterval(() => {
      api.get("/me/notifications")
        .then((r) => setNotifications(r.data || { items: [], unread: 0 }))
        .catch(() => {});
    }, 60000);
    return () => clearInterval(t);
  }, [user]);

  // Close notification dropdown on outside click
  useEffect(() => {
    if (!notifOpen) return;
    const onDocClick = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [notifOpen]);

  const openNotifs = async () => {
    setNotifOpen((v) => !v);
    if (!notifOpen && notifications.unread > 0) {
      try {
        await api.post("/me/notifications/read-all");
        setNotifications((n) => ({ ...n, unread: 0 }));
      } catch (e) {
        // best-effort
      }
    }
  };

  const copyCoupon = (e, code) => {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(code);
    toast.success(`Copied ${code}`);
  };

  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchOpen]);
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && searchOpen) {
        setSearchOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [searchOpen]);

  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) {
      navigate(`/shop?q=${encodeURIComponent(q)}`);
      setSearchOpen(false);
      setQ("");
    }
  };

  // Publish the actual rendered header height as a CSS variable on the root
  // element so sticky sidebars (PDP gallery, Cart/Checkout summary, CustomShop
  // filters) can position themselves with `top: calc(var(--header-h) + gap)`
  // instead of hard-coded pixel values that break whenever the header height
  // changes (announcement bar toggled, category bar wrap, etc.).
  const headerRef = useRef(null);
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const publish = () => {
      const h = el.offsetHeight;
      if (h) document.documentElement.style.setProperty("--header-h", `${h}px`);
    };
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    window.addEventListener("resize", publish);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", publish);
    };
  }, []);

  return (
    <header ref={headerRef} className="sticky top-0 z-50 backdrop-blur-xl bg-white/90 border-b border-zinc-200/80">
      {/* Announcement bar — FULL text editable from Admin → Settings → "Announcement bar text".
          NB: only renders once settings have actually loaded (typeof check) so the
          hard-coded fallback never flashes on first paint before /api/settings lands. */}
      {typeof settings.home_announce_bar === "string"
        && settings.home_announce_bar !== ""
        && settings.home_announce_bar !== "off" && (
        <div className="bg-white text-zinc-700 py-1.5 px-3 text-center leading-snug border-b border-zinc-200" style={{fontSize: '0.65rem', letterSpacing: '0.16em', textTransform: 'uppercase', fontWeight: 700}} data-testid="announce-bar">
          {settings.home_announce_bar}
        </div>
      )}

      <div className="max-w-[1400px] mx-auto px-4 md:px-8 flex items-center justify-between h-20 md:h-24">
        <div className="flex items-center gap-3 md:gap-4 lg:gap-8 min-w-0">
          <Link to="/" data-testid={HEADER.brand} className="flex items-center flex-shrink-0">
            <Logo size={96} />
          </Link>
          {/* Mobile + tablet only — gender pill sits next to the logo since
              the secondary mega strip (which hosts the desktop pill) is
              hidden under lg. Same active state, same testids. */}
          <div className="lg:hidden">
            <GenderPillToggle gender={gender} setGender={setGender} compact />
          </div>
          <nav className="hidden lg:flex items-center gap-4 lg:gap-6 text-sm font-medium">
            {navItems.map((n) => {
              const Ico = n.icon ? ICON_BY_NAME[n.icon] : null;
              const cls = navClassFor(n.style);
              const tid = n.id === "shop" ? HEADER.navShop : `header-nav-${n.id}`;
              const target = n.open_in_new_tab ? "_blank" : undefined;
              const isHeart = n.icon === "Heart";
              return (
                <Link key={n.id} to={n.href} target={target} rel={target ? "noreferrer" : undefined} data-testid={tid} className={cls}>
                  {Ico && (
                    <Ico
                      size={n.style === "highlight" ? 13 : 14}
                      {...(isHeart ? { fill: "#FF5722", stroke: "#FF5722" } : {})}
                    />
                  )} {n.label}
                </Link>
              );
            })}
            {/* AI Try-On — surfaced as a top-level nav entry so first-time
                visitors can discover the feature without drilling into a
                product. Distinct gradient pill matches the PDP card styling. */}
            <Link
              to="/try-on"
              data-testid="header-nav-tryon"
              className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200/60 text-[#FF5722] font-bold hover:shadow-sm transition-all"
            >
              <ScanFace size={13} /> Try On
              <span className="text-[9px] uppercase tracking-wider font-black bg-[#FF5722] text-white px-1 py-px rounded">AI</span>
            </Link>
          </nav>
        </div>

        <div className="flex items-center gap-0 sm:gap-1 md:gap-2">
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            className="hidden lg:inline-flex p-2 hover:text-[#FF5722] transition-colors"
            aria-label="Search"
            data-testid="header-search-icon"
          >
            <Search size={20} />
          </button>
          <Link to="/wishlist" data-testid={HEADER.wishlist} className="relative hidden lg:inline-flex p-2 hover:text-[#FF5722] transition-colors">
            <Heart size={20} />
            {wishlist.product_ids?.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-[#FF5722] text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">{wishlist.product_ids.length}</span>
            )}
          </Link>

          {user && (
            <div className="relative hidden lg:block" ref={notifRef}>
              <button
                type="button"
                onClick={openNotifs}
                className="relative p-2 hover:text-[#FF5722] transition-colors"
                aria-label="Notifications"
                data-testid="header-notif-btn"
              >
                <Bell size={20} />
                {notifications.unread > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#FF5722] text-white text-[10px] font-bold min-w-[16px] h-4 px-1 rounded-full flex items-center justify-center" data-testid="header-notif-count">
                    {notifications.unread > 9 ? "9+" : notifications.unread}
                  </span>
                )}
              </button>
              {notifOpen && (
                <div className="fixed left-4 right-4 top-[68px] sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-96 bg-white border border-zinc-200 shadow-2xl z-50 max-h-[480px] overflow-hidden flex flex-col rounded-xl" data-testid="header-notif-dropdown">
                  <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between">
                    <div className="font-display font-bold text-[#111111]">Notifications</div>
                    {notifications.items.length > 0 && (
                      <Link to="/account" onClick={() => setNotifOpen(false)} className="text-[11px] uppercase tracking-wider font-bold text-zinc-500 hover:text-[#FF5722]">View all</Link>
                    )}
                  </div>
                  <div className="overflow-y-auto flex-1">
                    {notifications.items.length === 0 ? (
                      <div className="px-4 py-10 text-center text-sm text-zinc-400">
                        <Bell size={26} className="mx-auto mb-2 text-zinc-300" />
                        You&apos;re all caught up.
                      </div>
                    ) : (
                      notifications.items.map((n) => (
                        <Link
                          key={n.id}
                          to={n.link || "/account"}
                          onClick={() => setNotifOpen(false)}
                          className={`block px-4 py-3 border-b border-zinc-100 hover:bg-zinc-50 transition-colors ${!n.is_read ? "bg-orange-50/40" : ""}`}
                          data-testid={`header-notif-${n.id}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-full bg-[#FF5722]/10 text-[#FF5722] flex items-center justify-center flex-shrink-0">
                              {n.type === "photo_of_the_week" ? <Award size={14} /> : <Bell size={14} />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-sm text-[#111111] truncate">{n.title}</div>
                              <div className="text-xs text-zinc-600 mt-0.5 line-clamp-2">{n.body}</div>
                              {n.meta?.coupon_code && (
                                <button
                                  type="button"
                                  onClick={(e) => copyCoupon(e, n.meta.coupon_code)}
                                  className="mt-1.5 inline-flex items-center gap-1 bg-[#FF5722] text-white font-mono text-[11px] font-bold tracking-wider px-2 py-0.5 hover:bg-[#FF5722] transition-colors rounded-md"
                                  data-testid={`header-notif-copy-${n.id}`}
                                >
                                  {n.meta.coupon_code} <Copy size={10} />
                                </button>
                              )}
                              <div className="text-[10px] text-zinc-400 uppercase tracking-wider mt-1">
                                {new Date(n.created_at).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                        </Link>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          <Link to="/cart" data-testid={HEADER.cart} className="relative p-2 hover:text-[#FF5722] transition-colors">
            <ShoppingBag size={20} />
            {cart.count > 0 && (
              <span className="absolute -top-1 -right-1 bg-[#FF5722] text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">{cart.count}</span>
            )}
          </Link>

          {user ? (
            <div className="relative">
              <button data-testid={HEADER.account} onClick={() => setOpen(!open)} className="flex items-center gap-2 px-2 lg:px-3 h-10 border border-transparent hover:border-zinc-300 transition-colors rounded-[10px]">
                <UserIcon size={16} />
                <span className="hidden lg:inline text-sm font-medium">{user.name?.split(" ")[0]}</span>
              </button>
              {open && (
                <div className="fixed left-4 right-4 top-[68px] sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-56 bg-white border border-zinc-200 shadow-lg z-50 rounded-xl overflow-hidden">
                  <Link to="/account" onClick={() => setOpen(false)} className="block px-4 py-3 text-sm hover:bg-zinc-50 border-b border-zinc-100">My Account</Link>
                  <Link to="/orders" onClick={() => setOpen(false)} className="block px-4 py-3 text-sm hover:bg-zinc-50 border-b border-zinc-100">My Orders</Link>
                  <Link to="/my-designs" onClick={() => setOpen(false)} className="block px-4 py-3 text-sm hover:bg-zinc-50 border-b border-zinc-100">My Designs</Link>
                  <Link to="/wishlist" onClick={() => setOpen(false)} className="block px-4 py-3 text-sm hover:bg-zinc-50 border-b border-zinc-100">My Wishlist</Link>
                  <Link to="/refer-and-earn" onClick={() => setOpen(false)} className="block px-4 py-3 text-sm hover:bg-zinc-50 border-b border-zinc-100 text-[#FF5722] font-semibold" data-testid="header-refer-link">Refer &amp; Earn 🎁</Link>
                  <Link to="/account#coupons" onClick={() => setOpen(false)} className="block px-4 py-3 text-sm hover:bg-zinc-50 border-b border-zinc-100">My Coupons</Link>
                  <Link to="/returns" onClick={() => setOpen(false)} className="block px-4 py-3 text-sm hover:bg-zinc-50 border-b border-zinc-100">My Returns</Link>
                  {(user.role === "designer" || user.role === "admin") && (
                    <Link to="/designer" onClick={() => setOpen(false)} className="block px-4 py-3 text-sm hover:bg-zinc-50 border-b border-zinc-100 text-[#FF5722] font-semibold" data-testid="header-designer-link">Designer Studio ✨</Link>
                  )}
                  {user.role === "admin" && (
                    <Link to="/admin" data-testid={HEADER.admin} onClick={() => setOpen(false)} className="px-4 py-3 text-sm hover:bg-zinc-50 border-b border-zinc-100 flex items-center gap-2"><Shield size={14} /> Admin Panel</Link>
                  )}
                  <button data-testid={HEADER.logout} onClick={async () => { await logout(); setOpen(false); navigate("/"); }} className="w-full text-left px-4 py-3 text-sm hover:bg-zinc-50 text-[#FF5722] flex items-center gap-2"><LogOut size={14} /> Logout</button>
                </div>
              )}
            </div>
          ) : (
            <>
              <Link to="/login" state={{ from: window.location.pathname + window.location.search }} data-testid={HEADER.login} className="text-sm font-medium hidden lg:inline px-3 py-2 hover:text-[#FF5722] transition-colors">Login</Link>
              <Link to="/signup" data-testid={HEADER.signup} className="hidden lg:inline-flex text-sm font-bold bg-[#FF5722] text-white px-3 md:px-4 py-2 md:py-2.5 hover:bg-orange-600 transition-colors whitespace-nowrap rounded-full">Sign Up</Link>
            </>
          )}

          <button onClick={() => setMenuOpen(!menuOpen)} className="lg:hidden p-1.5 sm:p-2" data-testid="header-menu-toggle">
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* ============ DESKTOP CATEGORY STRIP (mega menu) ============ */}
      <div
        className="hidden lg:block border-t border-zinc-200/60 bg-white relative"
        data-testid="header-mega-strip"
        onMouseLeave={() => setMegaOpenIdx(null)}
      >
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 relative flex items-stretch gap-4 md:gap-6 py-2">
          {/* Gender pill toggle — icon-only, drives home hero filtering.
              MEN (♂) | WOMEN (♀) | COUPLE (♥). Active pill uses brand
              orange #FF5722. */}
          <GenderPillToggle gender={gender} setGender={setGender} />
          {/* Vertical divider between the pill and the category strip */}
          <div className="flex-shrink-0 self-center h-5 w-px bg-zinc-200" aria-hidden="true" />
          <div className="flex-1 relative min-w-0 flex items-center">
          {/* Left edge fade + scroll-left button — only when content overflows.
              Positioned INSIDE the max-w container so they sit comfortably away
              from the screen wall on wide monitors. */}
          {megaEdges.left && (
            <>
              <div
                aria-hidden
                className="pointer-events-none absolute left-0 top-0 bottom-0 w-16 z-10 bg-gradient-to-r from-white via-white/95 to-transparent"
              />
              <button
                type="button"
                onClick={() => scrollMega(-1)}
                aria-label="Scroll categories left"
                data-testid="header-mega-scroll-left"
                className="absolute left-4 md:left-6 top-1/2 -translate-y-1/2 z-20 w-7 h-7 inline-flex items-center justify-center bg-white border border-zinc-200 hover:border-[#FF5722] hover:text-[#FF5722] shadow-sm rounded-full text-zinc-700 transition-colors"
              >
                <ChevronLeft size={14} />
              </button>
            </>
          )}
          {megaEdges.right && (
            <>
              <div
                aria-hidden
                className="pointer-events-none absolute right-0 top-0 bottom-0 w-16 z-10 bg-gradient-to-l from-white via-white/95 to-transparent"
              />
              <button
                type="button"
                onClick={() => scrollMega(1)}
                aria-label="Scroll categories right"
                data-testid="header-mega-scroll-right"
                className="absolute right-4 md:right-6 top-1/2 -translate-y-1/2 z-20 w-7 h-7 inline-flex items-center justify-center bg-white border border-zinc-200 hover:bg-[#FF5722] hover:border-[#FF5722] hover:text-white shadow-sm rounded-full text-zinc-700 transition-colors animate-pulse"
              >
                <ChevronRight size={14} />
              </button>
            </>
          )}
          <div ref={megaScrollRef} className="h-11 overflow-x-auto scrollbar-hide">
          <div className="flex items-center justify-start xl:justify-center gap-0 h-full w-max mx-auto">
            {menuItems.map((item, idx) => {
              const active = megaOpenIdx === idx;
              return (
                <div
                  key={item.label}
                  className="h-full flex items-center"
                  onMouseEnter={() => setMegaOpenIdx(idx)}
                >
                  <Link
                    to={item.demographic ? `/shop?demographic=${item.demographic}` : "/shop"}
                    data-testid={`header-mega-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className={`px-2.5 xl:px-3 h-full inline-flex items-center whitespace-nowrap text-[11px] xl:text-[12px] font-bold uppercase tracking-[0.1em] xl:tracking-[0.12em] transition-colors ${active ? "text-[#FF5722]" : "text-zinc-700 hover:text-[#FF5722]"} ${item.highlight ? "text-[#FF5722]" : ""}`}
                  >
                    {item.label}
                    <ChevronDown size={10} className={`ml-1 transition-transform ${active ? "rotate-180" : ""}`} />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
          </div>
        </div>
        {megaOpenIdx !== null && menuItems[megaOpenIdx]?.columns && (
          <div
            className="absolute inset-x-0 top-full bg-white border-t border-zinc-200 shadow-xl z-40 animate-in fade-in slide-in-from-top-1 duration-150"
            data-testid="header-mega-dropdown"
            onMouseEnter={() => setMegaOpenIdx(megaOpenIdx)}
            onMouseLeave={() => setMegaOpenIdx(null)}
          >
            <div className="max-w-[1400px] mx-auto px-8 py-8 grid grid-cols-12 gap-6">
              <div className="col-span-3">
                <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-400 font-bold mb-2">Category</div>
                <div className="font-display font-black text-3xl text-[#111111] leading-tight">{menuItems[megaOpenIdx].label}</div>
                <p className="mt-3 text-xs text-zinc-500 leading-relaxed">Explore curated picks &amp; bestsellers in this category. Customizable items flagged with the orange dot.</p>
                <Link
                  to={menuItems[megaOpenIdx].demographic ? `/shop?demographic=${menuItems[megaOpenIdx].demographic}` : "/shop"}
                  onClick={() => setMegaOpenIdx(null)}
                  className="inline-flex items-center gap-1.5 mt-4 text-[11px] uppercase tracking-wider font-bold text-[#FF5722] hover:underline"
                >
                  View all <ChevronRight size={12} />
                </Link>
              </div>
              {menuItems[megaOpenIdx].columns.map((col) => (
                <div key={col.title} className="col-span-3">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-400 font-bold mb-3">{col.title}</div>
                  <ul className="space-y-1.5">
                    {col.links.map((l) => (
                      <li key={l.label}>
                        <Link
                          to={l.to}
                          onClick={() => setMegaOpenIdx(null)}
                          data-testid={`header-mega-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          className="block text-sm text-zinc-700 hover:text-[#FF5722] hover:translate-x-0.5 transition-all duration-150 py-0.5"
                        >
                          {l.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {searchOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-[60] bg-zinc-900/40 backdrop-blur-sm animate-in fade-in duration-200"
            onClick={() => setSearchOpen(false)}
            data-testid="header-search-overlay"
          />
          
          {/* Modal Container */}
          <div className="fixed inset-x-0 top-20 md:top-24 z-[60] mx-auto max-w-3xl px-4 animate-in fade-in slide-in-from-top-4 duration-300 pointer-events-none">
            <div className="bg-white shadow-2xl border-2 border-zinc-300 overflow-hidden flex flex-col pointer-events-auto max-h-[80vh] rounded-2xl">
              {/* Input area */}
              <form onSubmit={submit} className="flex items-center gap-2 md:gap-4 px-3 md:px-6 h-14 sm:h-16 md:h-20 border-b border-zinc-100 relative bg-white">
                <Search size={20} className="text-[#111111] flex-shrink-0" />
                <input
                  ref={searchInputRef}
                  data-testid={HEADER.searchInput}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search products..."
                  className="flex-1 min-w-0 bg-transparent outline-none text-base sm:text-lg md:text-xl font-medium text-[#111111] placeholder:text-zinc-400"
                />
                {q && (
                  <button type="button" onClick={() => setQ("")} className="p-2 hover:text-[#FF5722] text-zinc-400 transition-colors" aria-label="Clear search">
                    <X size={20} />
                  </button>
                )}
                <div className="flex items-center gap-2 pl-2 sm:pl-4 border-l border-zinc-200">
                  <button
                    type="button"
                    onClick={() => { setSearchOpen(false); setQ(""); }}
                    className="flex items-center gap-1 px-2 sm:px-3 py-1.5 bg-zinc-100 hover:bg-zinc-200 text-zinc-600 text-xs font-bold tracking-wider uppercase transition-colors rounded-md"
                    aria-label="Close search"
                    data-testid="header-search-close"
                  >
                    <span className="hidden sm:inline">ESC</span>
                    <X size={16} className="sm:hidden" />
                  </button>
                </div>
              </form>

              {/* Suggestions Area */}
              <div className="bg-zinc-50 p-4 sm:p-6 md:p-8 overflow-y-auto">
                <h3 className="text-[10px] sm:text-xs font-bold text-zinc-500 uppercase tracking-widest mb-3 md:mb-4">Popular right now</h3>
                <div className="flex flex-wrap gap-2 sm:gap-3">
                  {["Custom T-Shirts", "Corporate Hoodies", "Coffee Mugs", "Sports Jerseys", "Tote Bags"].map((term) => (
                    <button
                      key={term}
                      type="button"
                      onClick={() => {
                        setQ(term);
                        navigate(`/shop?q=${encodeURIComponent(term)}`);
                        setSearchOpen(false);
                        setQ("");
                      }}
                      className="px-3 sm:px-5 h-9 sm:h-10 bg-white border border-zinc-200 text-xs sm:text-sm font-medium hover:border-[#FF5722] hover:text-[#FF5722] hover:-translate-y-0.5 transition-all duration-200 shadow-sm rounded-full"
                    >
                      {term}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ============ MOBILE NAV DRAWER ============ */}
      {menuOpen && createPortal(
        <>
          {/* Backdrop */}
          <div
            onClick={() => setMenuOpen(false)}
            className="lg:hidden fixed inset-0 z-[90] bg-black/50 backdrop-blur-sm animate-in fade-in duration-200"
            data-testid="header-mobile-backdrop"
          />
          {/* Slide-in panel */}
          <div
            className="lg:hidden fixed top-0 right-0 bottom-0 z-[100] w-[88vw] max-w-[400px] bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-300"
            data-testid="header-mobile-drawer"
          >
            {/* Top — user card / sign-in CTA */}
            <div className="bg-white border-b border-zinc-100 px-5 pt-6 pb-5 relative">
              <button
                onClick={() => setMenuOpen(false)}
                className="absolute top-3 right-3 w-9 h-9 inline-flex items-center justify-center rounded-full text-zinc-500 hover:bg-zinc-100 hover:text-[#111111]"
                data-testid="header-mobile-close"
                aria-label="Close menu"
              >
                <X size={18} />
              </button>
              {user ? (
                <Link
                  to="/account"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-3"
                  data-testid="header-mobile-account-card"
                >
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#FF5722] to-[#e64a19] text-white flex items-center justify-center font-bold text-lg shadow-sm">
                    {(user.name?.[0] || user.email?.[0] || "?").toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold">Welcome back</div>
                    <div className="font-bold text-base text-[#111111] truncate">{user.name || user.email}</div>
                    <div className="text-[11px] text-zinc-500 truncate">{user.email}</div>
                  </div>
                  <ChevronRight size={18} className="text-zinc-300" />
                </Link>
              ) : (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold">Welcome</div>
                  <div className="mt-1 font-display font-black text-2xl tracking-tighter text-[#111111]">Hello, friend</div>
                  <div className="text-xs text-zinc-500 mt-1">Sign in to track orders, save designs, and earn rewards.</div>
                  <div className="flex items-center gap-2 mt-4">
                    <Link
                      to="/login"
                      onClick={() => setMenuOpen(false)}
                      data-testid="header-mobile-login"
                      className="flex-1 h-10 inline-flex items-center justify-center rounded-full bg-[#FF5722] text-white font-bold text-xs uppercase tracking-wider hover:bg-[#111111] transition-colors"
                    >
                      Sign in
                    </Link>
                    <Link
                      to="/register"
                      onClick={() => setMenuOpen(false)}
                      data-testid="header-mobile-register"
                      className="flex-1 h-10 inline-flex items-center justify-center rounded-full border-2 border-zinc-200 text-[#111111] font-bold text-xs uppercase tracking-wider hover:border-[#FF5722] hover:text-[#FF5722]"
                    >
                      Register
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* Search */}
            <div className="px-4 py-3 border-b border-zinc-100">
              <form onSubmit={submit} className="flex items-center gap-2 border-2 border-zinc-200 px-3 h-11 bg-zinc-50 focus-within:border-[#FF5722] focus-within:bg-white transition-colors rounded-xl">
                <Search size={16} className="text-zinc-400" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search products, designs..."
                  className="bg-transparent outline-none w-full text-sm font-medium text-[#111111]"
                  data-testid="header-mobile-search"
                />
              </form>
            </div>

            {/* Quick action chips (only when logged in) */}
            {user && (
              <div className="px-4 pt-3 pb-2 grid grid-cols-4 gap-2">
                <MobileQuickChip to="/orders" icon={Package} label="Orders" onClick={() => setMenuOpen(false)} />
                <MobileQuickChip to="/wishlist" icon={Heart} label="Wishlist" count={wishlist?.product_ids?.length} onClick={() => setMenuOpen(false)} />
                <MobileQuickChip to="/cart" icon={ShoppingBag} label="Cart" count={cart?.count} onClick={() => setMenuOpen(false)} />
                <MobileQuickChip to="/account?tab=notifications" icon={Bell} label="Alerts" count={notifications?.unread} onClick={() => setMenuOpen(false)} />
              </div>
            )}

            {/* Main nav */}
            <div className="flex-1 overflow-y-auto py-2">
              <MobileNavSection title="Shop">
                <MobileNavItem to="/" icon={Home} label="Home" onClick={() => setMenuOpen(false)} />
                {navItems.map((n) => {
                  const Ico = n.icon ? ICON_BY_NAME[n.icon] : ShoppingBag;
                  return (
                    <MobileNavItem
                      key={n.id}
                      to={n.href}
                      icon={Ico}
                      label={n.label}
                      highlight={n.style === "highlight" || n.style === "primary"}
                      onClick={() => setMenuOpen(false)}
                    />
                  );
                })}
                <MobileNavItem
                  to="/try-on"
                  icon={ScanFace}
                  label="AI Try-On"
                  highlight
                  onClick={() => setMenuOpen(false)}
                />
              </MobileNavSection>

              <MobileNavSection title="Categories" icon={Layers}>
                {menuItems.map((item, idx) => {
                  const expanded = mobileMegaIdx === idx;
                  return (
                    <div key={item.label} className="border-b border-zinc-50 last:border-b-0">
                      <button
                        type="button"
                        onClick={() => setMobileMegaIdx(expanded ? null : idx)}
                        data-testid={`header-mobile-mega-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className={`w-full flex items-center justify-between pl-6 pr-5 py-2.5 text-sm hover:bg-zinc-50 active:bg-zinc-100 transition-colors ${item.highlight ? "text-[#FF5722] font-bold" : "font-medium text-zinc-700"}`}
                      >
                        <span className="inline-flex items-center gap-2">
                          {item.highlight && <Sparkles size={12} />}
                          {item.label}
                        </span>
                        <ChevronDown size={14} className={`text-zinc-400 transition-transform ${expanded ? "rotate-180" : ""}`} />
                      </button>
                      {expanded && (
                        <div className="bg-zinc-50/60 py-1">
                          <Link
                            to={item.demographic ? `/shop?demographic=${item.demographic}` : "/shop"}
                            onClick={() => setMenuOpen(false)}
                            className="block pl-10 pr-5 py-2 text-[12px] font-bold uppercase tracking-wider text-[#FF5722]"
                          >
                            View all →
                          </Link>
                          {(item.columns || []).flatMap((col) => col.links).map((l) => (
                            <Link
                              key={l.to}
                              to={l.to}
                              onClick={() => setMenuOpen(false)}
                              className="block pl-10 pr-5 py-2 text-sm text-zinc-600 hover:text-[#FF5722] hover:bg-white"
                            >
                              {l.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </MobileNavSection>

              {user && (
                <MobileNavSection title="My Account">
                  <MobileNavItem to="/account/profile" icon={UserIcon} label="Profile" onClick={() => setMenuOpen(false)} />
                  <MobileNavItem to="/account/addresses" icon={MapPin} label="Address Book" onClick={() => setMenuOpen(false)} />
                  <MobileNavItem to="/account" icon={Award} label="Rewards & Points" onClick={() => setMenuOpen(false)} />
                  {user.role === "admin" && (
                    <MobileNavItem to="/admin" icon={Shield} label="Admin Panel" highlight onClick={() => setMenuOpen(false)} />
                  )}
                </MobileNavSection>
              )}

              {/* Support — only render when admin has set contact details */}
              {(settings?.support_email || settings?.support_phone) && (
                <MobileNavSection title="Support">
                  {settings?.support_email && (
                    <a
                      href={`mailto:${settings.support_email}`}
                      onClick={() => setMenuOpen(false)}
                      className="flex items-center gap-3 px-5 py-2.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50"
                    >
                      <Mail size={16} className="text-zinc-400" />
                      <span className="flex-1">Email Support</span>
                      <ChevronRight size={14} className="text-zinc-300" />
                    </a>
                  )}
                  {settings?.support_phone && (
                    <a
                      href={`tel:${settings.support_phone}`}
                      onClick={() => setMenuOpen(false)}
                      className="flex items-center gap-3 px-5 py-2.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50"
                    >
                      <Phone size={16} className="text-zinc-400" />
                      <span className="flex-1">Call Us</span>
                      <ChevronRight size={14} className="text-zinc-300" />
                    </a>
                  )}
                </MobileNavSection>
              )}
            </div>

            {/* Sign-out footer */}
            {user && (
              <div className="px-4 py-3 border-t border-zinc-100">
                <button
                  onClick={() => { logout(); setMenuOpen(false); navigate("/"); }}
                  data-testid="header-mobile-logout"
                  className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-xl border-2 border-red-100 text-red-600 font-bold text-xs uppercase tracking-wider hover:bg-red-50 transition-colors"
                >
                  <LogOut size={14} /> Sign out
                </button>
              </div>
            )}
          </div>
        </>,
        document.body
      )}
    </header>
  );
}

/** Top quick-action chip rendered above the main nav (Orders / Wishlist / Cart) */
function MobileQuickChip({ to, icon: Icon, label, count, onClick }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="flex flex-col items-center justify-center gap-1 py-3 rounded-2xl bg-zinc-50 hover:bg-orange-50 active:bg-orange-100 transition-colors text-zinc-700 hover:text-[#FF5722] relative"
      data-testid={`header-mobile-chip-${label.toLowerCase()}`}
    >
      <Icon size={18} />
      <span className="text-[10px] uppercase tracking-wider font-bold">{label}</span>
      {count > 0 && (
        <span className="absolute top-1 right-3 min-w-[16px] h-4 px-1 bg-[#FF5722] text-white text-[9px] font-bold rounded-full inline-flex items-center justify-center">
          {count > 99 ? "99+" : count}
        </span>
      )}
    </Link>
  );
}

/** Grouped section header with optional icon */
function MobileNavSection({ title, icon: Icon, children }) {
  return (
    <div className="mt-3 mb-1">
      <div className="px-5 pb-1 text-[10px] uppercase tracking-wider font-bold text-zinc-400 flex items-center gap-1.5">
        {Icon && <Icon size={11} />}
        {title}
      </div>
      {children}
    </div>
  );
}

/** Standard mobile nav row */
function MobileNavItem({ to, icon: Icon, label, highlight, badge, onClick }) {
  // Heart icon → render solid orange filled (matches desktop Couple link treatment)
  const isHeart = Icon === Heart;
  return (
    <Link
      to={to}
      onClick={onClick}
      data-testid={`header-mobile-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={`flex items-center gap-3 px-5 py-2.5 text-sm hover:bg-zinc-50 active:bg-zinc-100 transition-colors ${highlight ? "text-[#FF5722] font-bold" : "font-medium text-zinc-700"}`}
    >
      <Icon
        size={16}
        className={highlight || isHeart ? "text-[#FF5722]" : "text-zinc-400"}
        {...(isHeart ? { fill: "#FF5722", stroke: "#FF5722" } : {})}
      />
      <span className="flex-1">{label}</span>
      {badge && (
        <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 bg-[#FF5722] text-white rounded-full">{badge}</span>
      )}
      <ChevronRight size={14} className="text-zinc-300" />
    </Link>
  );
}


// Shared MEN | WOMEN | COUPLE icon-only pill toggle. Same testids regardless
// of where it's mounted (desktop mega-strip + mobile header) so e2e tests
// stay simple. Hover title attribute surfaces "Shop Men/Women/Couple" labels
// as native browser tooltips so the icons stay self-explanatory.
function GenderPillToggle({ gender, setGender, compact = false }) {
  const items = [
    { id: "men",    label: "Men",    Icon: Mars },
    { id: "women",  label: "Women",  Icon: Venus },
    { id: "couple", label: "Couple", Icon: Heart },
  ];
  // Bigger touch target on mobile (compact mode lives next to the logo on
  // phones/tablets). 36×32 px easily passes Apple's 44pt touch heuristic
  // when combined with the surrounding pill padding.
  const btnSize = compact ? "w-9 h-8" : "w-9 h-8";
  const iconSize = compact ? 16 : 17;
  return (
    <div
      className="flex-shrink-0 self-center inline-flex items-center bg-zinc-100 rounded-full p-1 gap-0.5"
      data-testid="header-gender-toggle"
      role="group"
      aria-label="Shop by gender"
    >
      {items.map((g) => {
        const Icon = g.Icon;
        return (
          <button
            key={g.id}
            type="button"
            onClick={() => setGender(g.id)}
            aria-label={g.label}
            aria-pressed={gender === g.id}
            title={g.label}
            data-testid={`header-gender-${g.id}`}
            className={`relative group ${btnSize} inline-flex items-center justify-center rounded-full transition-colors ${
              gender === g.id
                ? "bg-[#FF5722] text-white shadow-sm"
                : "text-zinc-600 hover:text-[#FF5722]"
            }`}
          >
            <Icon size={iconSize} strokeWidth={gender === g.id ? 2.5 : 2.25} aria-hidden="true" />
            {/* Custom hover tooltip — appears instantly (no native 700ms delay)
                and is hidden on touch devices to avoid double-tap weirdness. */}
            <span
              role="tooltip"
              className="pointer-events-none absolute top-full left-1/2 -translate-x-1/2 mt-1.5 px-2 py-0.5 rounded-md bg-zinc-900 text-white text-[10px] font-bold uppercase tracking-wider whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-50 hidden md:block"
            >
              {g.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}
